import React from "react";
import { useNavigate } from "react-router-dom";
import { getUsers } from "../../utils/getUser";
import { base64ToFile } from "../../utils/util";
import { stringToColor } from "../member/tierConfig";

const ROLE_LABELS = {
  SUPERADMIN: "Super Admin",
  MANAGER: "Manager",
};

// Only roles with a registered /dashboard/my-profile route (see App.js
// AuthenticatedRoutes) navigate there on click.
const PROFILE_ROUTE_ROLES = new Set(["SUPERADMIN", "MANAGER"]);

export default function ManagementProfileCard({ collapsed = false, onNavigate }) {
  const navigate = useNavigate();
  const user = getUsers();

  if (!user) return null;

  const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  const initials = `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`;
  const roleLabel = ROLE_LABELS[user.role] || user.role || "";
  const canOpenProfile = PROFILE_ROUTE_ROLES.has(user.role);

  let avatarSrc = null;
  if (user.profile_picture) {
    try {
      avatarSrc = URL.createObjectURL(
        base64ToFile(user.profile_picture, "avatar", "image/*")
      );
    } catch {
      avatarSrc = null;
    }
  }

  return (
    <div
      className="tw-p-3 hover:tw-bg-white/[0.03] tw-transition-colors"
      style={{ padding: 12, cursor: canOpenProfile ? "pointer" : "default" }}
      onClick={
        canOpenProfile
          ? () => {
              navigate("/dashboard/my-profile");
              onNavigate?.();
            }
          : undefined
      }
      title={collapsed ? name : undefined}
    >
      <div
        className="tw-flex tw-items-center"
        style={{ display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start" }}
      >
        <div
          className="tw-relative tw-w-9 tw-h-9 tw-shrink-0"
          style={{
            position: "relative",
            width: 36,
            height: 36,
            marginRight: collapsed ? 0 : 10,
            flexShrink: 0,
            transition: "margin-right 0.2s ease",
          }}
        >
          <svg
            className="tw-absolute tw-inset-0 tw-w-full tw-h-full"
            viewBox="0 0 36 36"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          >
            <circle
              cx="18"
              cy="18"
              r="16"
              fill="none"
              stroke="rgba(255,255,255,0.06)"
              strokeWidth="2.5"
            />
            <circle
              cx="18"
              cy="18"
              r="16"
              fill="none"
              stroke="#00ADB5"
              strokeWidth="2.5"
              strokeDasharray="100.5 0"
              strokeLinecap="round"
              transform="rotate(-90 18 18)"
            />
          </svg>
          <div
            className="tw-absolute tw-inset-[3px] tw-rounded-full tw-overflow-hidden tw-flex tw-items-center tw-justify-center"
            style={{ position: "absolute", inset: 3, borderRadius: "50%", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt={name}
                className="tw-w-full tw-h-full tw-object-cover tw-rounded-full"
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
              />
            ) : (
              <div
                className="tw-w-full tw-h-full tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-[11px] tw-font-bold tw-text-white"
                style={{ width: "100%", height: "100%", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", backgroundColor: stringToColor(name || "User") }}
              >
                {initials}
              </div>
            )}
          </div>
        </div>

        <div
          className="tw-min-w-0"
          style={{
            minWidth: 0,
            flex: collapsed ? "0 0 0px" : "1 1 auto",
            maxWidth: collapsed ? 0 : 220,
            opacity: collapsed ? 0 : 1,
            overflow: "hidden",
            transition: "opacity 0.16s ease, max-width 0.22s ease",
          }}
        >
          <div
            className="tw-text-[12px] tw-font-bold tw-text-white tw-truncate"
            style={{ fontSize: 12, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {name}
          </div>
          <div
            className="tw-text-[10px] tw-font-extrabold tw-tracking-[0.05em]"
            style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.05em", color: "#00ADB5", whiteSpace: "nowrap" }}
          >
            {roleLabel}
          </div>
        </div>
      </div>
    </div>
  );
}
